import { useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { IconChevronLeft, IconChevronRight } from '../../components/icons'
import { Button } from '../../components/ui/Button'
import { EmptyState } from '../../components/ui/EmptyState'
import { Skeleton } from '../../components/ui/Skeleton'
import { isSafeInternalPath } from './catalog-brand-link'
import { FeaturedCategories } from './components/FeaturedCategories'
import { RecentlyViewed } from './components/RecentlyViewed'
import { ShopByBrand } from './components/ShopByBrand'
import { ShopByCategory } from './components/ShopByCategory'
import { deriveFeaturedCategoryCards } from './featuredCategoryCards'
import { useCatalogBrands } from './useCatalogBrands'
import type { BrandListingService } from './useCatalogBrands'
import { defaultCatalogSearchService } from './useCatalogSearch'
import { useCategoryTaxonomy } from './useCategoryTaxonomy'
import type { CategoryListingService } from './useCategoryTaxonomy'
import { useRecentlyViewed } from './useRecentlyViewed'

export interface CatalogBrowsePageProps {
  /** Injectable for tests (mirrors every other vertical page's `service`
   * prop); defaults to the real BFF-backed `GET /catalog/categories`. */
  readonly service?: CategoryListingService
  /** Injectable for tests; defaults to the real BFF-backed
   * `GET /catalog/brands` (UID-416 / ADR-0131). */
  readonly brandService?: BrandListingService
}

/**
 * `/catalog`'s default landing (UID-253 rebuild, ADR-0091). A prior UID-253
 * pass built this route as a flat "All products" `ResultCard` grid modeled
 * on `catalog.png` + the README's prose; inspecting the actual interactive
 * prototype (`Fictiv Platform.dc.html`) showed the real `browse` stage is
 * NOT that grid — it's a greeting + AI suggestion pills + "Recently viewed"
 * + "Shop by category" + "Featured categories". Per the product decision
 * that scoped this rebuild, this route renders "What are you sourcing
 * today?" + "Recently viewed" + "Shop by category" for now:
 *   - "Recently viewed" is LIVE (`<RecentlyViewed>`) and is now REAL,
 *     per-user view-history tracking, kept 100% client-side (no server, no
 *     migration — the revived, cost-free alternative to ADR-0127's rejected
 *     server-side design; see `recently-viewed-store.ts`). This page reads
 *     `useRecentlyViewed()` itself (not just `<RecentlyViewed>`) so it can
 *     hide the WHOLE section — heading and Prev/Next chrome included — when
 *     the shopper hasn't viewed anything yet; there is no curated fallback;
 *   - "Featured categories" is now LIVE (UID-432 / ADR-0133) —
 *     `<FeaturedCategories>`, derived synchronously from the SAME
 *     `useCategoryTaxonomy()` state and `categoryDetails` map this page
 *     already builds for "Shop by category" (zero new fetches, zero
 *     contract change). It occupies the left (`1.5fr`) cell of the bottom
 *     two-column row, opposite "Shop by brand";
 *   - "Shop by brand" is now LIVE (UID-416 / ADR-0131) — `<ShopByBrand>`,
 *     fetched via `useCatalogBrands`, in the right (`1fr`) cell of the
 *     bottom two-column row, below "Shop by category". A Catalog-service
 *     failure or an empty brand list hides the whole panel (no error card,
 *     no skeleton) rather than degrading this page, whose primary job is
 *     "Shop by category".
 *   - the AI suggestion pills (UID-36) now have a real destination — the
 *     sourcing-agent thread (`AgentSearchPage`) — so they're wired below
 *     rather than omitted; the four labels are ported verbatim from the
 *     design source's `suggestions` array (`Fictiv Platform.dc.html`
 *     ~line 10087), each handing its label off to `/agent?q=...`;
 *   - the greeting/search-box header is also dropped — search lives in the
 *     top bar (UID-251), not on this page.
 *
 * `ResultCard`/`CategoryFilterBar`/`CatalogResultThumb`/`useCatalogSearch`
 * are NOT deleted — they still back UID-251's actual search-results screen
 * and UID-252/UID-34's category-listing screen once those are built; this
 * page just stops wiring them into `/catalog`'s default render.
 *
 * Category-chip click: navigates to the series-card grid for that leaf
 * category (UID-286, `CatalogSeriesPage.tsx`) — the real destination per the
 * architect's flow reconciliation
 * (`docs/design/catalog-configurator-surfaces-reconciliation.md` §3). This
 * REPLACES the prior interim "not built yet" toast now that the real
 * destination exists.
 */

/** The design source's four canned AI-suggestion labels, verbatim
 * (`Fictiv Platform.dc.html` ~line 10087's `suggestions` array). */
const SOURCING_SUGGESTIONS = [
  'Find a 20mm linear guide in stock',
  'Bearing for a 25mm shaft',
  'Fasteners for a 6061 bracket',
  'Spec parts for an XY gantry',
] as const

export function CatalogBrowsePage({
  service = defaultCatalogSearchService,
  brandService,
}: CatalogBrowsePageProps) {
  const state = useCategoryTaxonomy(service)
  const brandsState = useCatalogBrands(brandService)
  const categoryDetails = useMemo(
    () =>
      new Map(
        state.status === 'ready'
          ? state.categories.map((category) => [category.name, category])
          : [],
      ),
    [state],
  )
  // UID-432 / ADR-0133 D4: derived synchronously from the SAME
  // `useCategoryTaxonomy()` groups + `categoryDetails` map above — zero new
  // fetches, zero contract change. Any featured group unbacked by real data
  // is simply omitted (ADR-0133 D5), never fabricated or padded.
  const featuredCards = useMemo(
    () =>
      deriveFeaturedCategoryCards(state.status === 'ready' ? state.groups : [], categoryDetails),
    [state, categoryDetails],
  )
  const navigate = useNavigate()
  const recentlyViewedScrollRef = useRef<HTMLDivElement>(null)
  // Read here too (not just inside `<RecentlyViewed>`) so this page can hide
  // the heading + Prev/Next chrome it owns alongside the row itself — the
  // user's explicit "hide the ENTIRE section" decision, no curated fallback.
  const recentlyViewedEntries = useRecentlyViewed()

  function handleCategoryClick(category: string) {
    navigate(`/catalog/series/${encodeURIComponent(category)}`)
  }

  /**
   * UID-416 / ADR-0131 D10: `CatalogBrowsePage` owns navigation for the brand
   * panel, same as `handleCategoryClick` above. Client-side defence in depth
   * (D4) — validate `linkPath` again before navigating, even though the BFF
   * already validated it server-side; an unsafe path is silently dropped
   * (never navigated to) rather than surfaced as a broken click.
   */
  function handleBrandClick(linkPath: string) {
    if (!isSafeInternalPath(linkPath)) return
    navigate(linkPath)
  }

  function handleSuggestionClick(label: string) {
    navigate(`/agent?q=${encodeURIComponent(label)}`)
  }

  /** Same Prev/Next split as `HomePage`'s `HomeCategoryCarousel` (ADR-0128
   * Decision 5): the row forwards its scroll container's ref, and THIS
   * page — not the row — owns and drives the Prev/Next buttons, matching
   * where the design source puts them (beside the section caption,
   * `Fictiv Platform.dc.html` ~L848-849). */
  function scrollRecentlyViewed(direction: 1 | -1) {
    recentlyViewedScrollRef.current?.scrollBy({ left: direction * 520, behavior: 'smooth' })
  }

  return (
    <section className="catalog-storefront" aria-label="Catalog">
      <div className="catalog-sourcing-prompt">
        <h2 className="catalog-sourcing-heading">What are you sourcing today?</h2>
        <div className="catalog-sourcing-chips">
          {SOURCING_SUGGESTIONS.map((label) => (
            <button
              key={label}
              type="button"
              className="catalog-sourcing-chip"
              onClick={() => handleSuggestionClick(label)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {recentlyViewedEntries.length > 0 && (
        <section aria-labelledby="recently-viewed-heading">
          <div className="home-catalog-heading-row">
            <h2 id="recently-viewed-heading" className="catalog-shop-by-category-heading">
              Recently viewed
            </h2>
            <div className="home-category-carousel-nav">
              <button
                type="button"
                className="catalog-recently-viewed-nav-btn"
                aria-label="Previous recently viewed parts"
                onClick={() => scrollRecentlyViewed(-1)}
              >
                <IconChevronLeft size={16} strokeWidth={2.4} />
              </button>
              <button
                type="button"
                className="catalog-recently-viewed-nav-btn"
                aria-label="Next recently viewed parts"
                onClick={() => scrollRecentlyViewed(1)}
              >
                <IconChevronRight size={16} strokeWidth={2.4} />
              </button>
            </div>
          </div>
          <RecentlyViewed ref={recentlyViewedScrollRef} />
        </section>
      )}

      {state.status === 'loading' && (
        <output className="catalog-group-grid" aria-busy="true">
          <span className="sr-only">Loading categories…</span>
          {Array.from({ length: 4 }, (_, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: a fixed-count skeleton grid has no stable identity to key by.
            <Skeleton key={index} height={160} variant="block" />
          ))}
        </output>
      )}

      {state.status === 'error' && (
        <EmptyState
          title="We couldn’t load the catalog categories"
          detail={state.message}
          action={<Button onClick={state.retry}>Try again</Button>}
        />
      )}

      {state.status === 'ready' && state.groups.length === 0 && state.other.length === 0 && (
        <EmptyState
          title="No categories to browse yet"
          detail="The catalog category list came back empty."
        />
      )}

      {state.status === 'ready' && (state.groups.length > 0 || state.other.length > 0) && (
        <ShopByCategory
          groups={state.groups}
          other={state.other}
          categoryDetails={categoryDetails}
          onCategoryClick={handleCategoryClick}
        />
      )}

      {/* UID-432 / ADR-0133 + UID-416 / ADR-0131 D10 (mirroring apis
       * ADR-0011 D8/D10.2/D10.3): the mock's bottom two-column row —
       * "Featured categories" (1.5fr) + "Shop by brand" (1fr). The row
       * itself always renders whenever the brand panel is ready, so the
       * 1.5fr/1fr ratio never changes (D10.3); ONLY the left cell is
       * conditional, on whether at least one featured group is backed by
       * real data (ADR-0133 D5.6: if all four end up unbacked, that cell
       * renders absent exactly as it does today — no error card, no
       * skeleton, no filler). */}
      {brandsState.status === 'ready' && (
        <div className="catalog-bottom-row">
          {featuredCards.length > 0 ? (
            <FeaturedCategories cards={featuredCards} onFeaturedGroupClick={handleCategoryClick} />
          ) : (
            <div />
          )}
          <ShopByBrand brands={brandsState.brands} onBrandClick={handleBrandClick} />
        </div>
      )}
    </section>
  )
}
